import React, { useState } from 'react';
import { useForm } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, Code, Info, AlertCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

interface Document { id: number; name: string; }
interface ExtractJob { id: number; job_id: string; name?: string; }

interface DivisorMassFormProps {
  document: Document;
  extractJob: ExtractJob;
  divisorTypes: string[];
  errors?: Record<string, string[]>;
}

export default function DivisorMassForm({
  document,
  extractJob,
  divisorTypes,
  errors = {}
}: DivisorMassFormProps) {
  const [selectedDivisorTypes, setSelectedDivisorTypes] = useState<string[]>([]);

  const {
    data,
    setData,
    post,
    processing,
    errors: formErrors,
    transform,
  } = useForm({
    structure: '',
    divisor_types: [] as string[]
  });

  const allErrors = { ...errors, ...formErrors };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate structure is not empty
    if (!data.structure.trim()) {
      alert('Please provide a JSON structure');
      return;
    }

    // Validate divisor types are selected
    if (selectedDivisorTypes.length === 0) {
      alert('Please select at least one divisor type');
      return;
    }

    // Validate JSON structure
    try {
      JSON.parse(data.structure);
    } catch (e) {
      alert('Please provide valid JSON structure');
      return;
    }

    // Transform data to include selected divisor types
    transform(() => ({
      structure: data.structure,
      divisor_types: selectedDivisorTypes
    }));

    // Submit form
    post(`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors/mass_create`);
  };

  const handleDivisorTypeChange = (type: string, checked: boolean) => {
    if (checked) {
      setSelectedDivisorTypes(prev => [...prev, type]);
    } else {
      setSelectedDivisorTypes(prev => prev.filter(t => t !== type));
    }
  };

  const generateSampleStructure = () => {
    if (selectedDivisorTypes.length === 0) {
      alert('Please select divisor types first');
      return;
    }

    // Recursive function to build nested structure
    const buildLevel = (typeIndex: number, parentNumber = ''): any[] => {
      if (typeIndex >= selectedDivisorTypes.length) {
        return [];
      }

      const currentType = selectedDivisorTypes[typeIndex];
      const isLastLevel = typeIndex === selectedDivisorTypes.length - 1;
      
      return [1, 2].map(i => {
        const number = parentNumber ? `${parentNumber}.${i}` : `${i}`;
        const item: any = {
          "title": `${number} ${currentType.charAt(0).toUpperCase() + currentType.slice(1)}`,
          "content_preview": `This ${currentType} contains ${isLastLevel ? 'specific details and content' : 'subsections with detailed information'}.`
        };

        // If not the last level, add children
        if (!isLastLevel) {
          const nextType = selectedDivisorTypes[typeIndex + 1];
          const nextTypePlural = nextType + 's';
          item[nextTypePlural] = buildLevel(typeIndex + 1, number);
        }

        return item;
      });
    };

    // Generate the root structure
    const topLevel = selectedDivisorTypes[0];
    const topLevelPlural = topLevel + 's';
    const sample = {
      [topLevelPlural]: buildLevel(0)
    };
    
    setData('structure', JSON.stringify(sample, null, 2));
  };

  const hasAnyErrors = Object.keys(allErrors).some((k) => {
    const fieldErrors = allErrors[k as keyof typeof allErrors];
    return Array.isArray(fieldErrors) && fieldErrors.length > 0;
  });

  return (
    <div className="space-y-6">
      {hasAnyErrors && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-semibold mb-2">Please fix the following errors:</div>
            <ul className="list-disc list-inside space-y-1">
              {Object.entries(allErrors).map(([field, fieldErrors]) => {
                if (!Array.isArray(fieldErrors)) return null;
                return fieldErrors.map((error: string, index: number) => (
                  <li key={`${field}-${index}`}>{error}</li>
                ));
              })}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Divisor Types Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Divisor Type Hierarchy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Select the divisor types in hierarchical order. For example: if you have chapters containing sections, 
              select "chapter" first, then "section". The JSON structure should use the plural form of these types as keys.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {divisorTypes.map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={`type-${type}`}
                    checked={selectedDivisorTypes.includes(type)}
                    onCheckedChange={(checked) => handleDivisorTypeChange(type, checked as boolean)}
                  />
                  <Label htmlFor={`type-${type}`} className="text-sm">
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Label>
                </div>
              ))}
            </div>

            {selectedDivisorTypes.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm font-medium text-blue-800">Selected hierarchy:</div>
                <div className="text-sm text-blue-700">
                  {selectedDivisorTypes.join(' → ')}
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  JSON should use keys: {selectedDivisorTypes.map(t => `"${t}s"`).join(', ')}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* JSON Structure */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              JSON Structure
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={generateSampleStructure}
                className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800"
              >
                Generate Sample JSON Structure
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="structure">JSON Structure</Label>
              <Textarea
                id="structure"
                value={data.structure}
                onChange={(e) => setData('structure', e.target.value)}
                className="min-h-[400px] font-mono text-sm"
                placeholder='Enter JSON structure with hierarchical divisors...'
                rows={20}
              />
              <div className="text-xs text-gray-600">
                Enter a nested JSON structure where each level corresponds to your selected divisor types.
                Each divisor should have at least "title" and "content_preview" fields.
              </div>
            </div>

            {/* JSON Structure Help */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">JSON Structure Guidelines:</div>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Use plural forms of divisor types as keys (e.g., "chapters", "sections")</li>
                  <li>Each divisor must have "title" and "content_preview" fields</li>
                  <li>Nest child divisors under their parent using the appropriate key</li>
                  <li>The hierarchy order should match your selected divisor types above</li>
                </ul>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-center pt-6">
          <Button 
            type="submit" 
            disabled={processing || selectedDivisorTypes.length === 0 || !data.structure.trim()} 
            size="lg" 
            className="px-8"
          >
            {processing ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating Divisors...
              </div>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Create Divisors from JSON
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}